import { makeStyles, MenuItem, Theme } from "@material-ui/core";
import { FilterList } from "@material-ui/icons";
import React from "react";

import {
  Filter,
  FilterCheckbox,
  FilterMenu,
  FilterRadioButton,
  FilterSearch,
} from "@ofqual/portal-components/components/ResponseFilter";

import { useLookup, useSearch, useUrl } from "@ofqual/portal-core/core/hooks";
import { Select } from "@ofqual/portal-components/components/Select";
import { DatePicker } from "@ofqual/portal-components/components/DatePicker";
import { foiStatusService, foiTypeService, usersService, foiStageService } from "../../services";
import { ensureParamIsArray } from "@ofqual/portal-core/core/utils/urlHelper";
import { Opts } from "@ofqual/portal-core/core/types";

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      "& fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    menuItem: {
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
  };
});

export const FilterControls: React.FC = () => {
  const classes = useStyles();

  const {
    searchParams,
    navigateToNextSearch,
    searchParamsFiltersOnly,
  } = useSearch(useUrl());

  const [statusLookups, typeLookups, usersLookups, foiStageLookups] = useLookup(
    foiStatusService,
    foiTypeService,
    {
      service: { getAll: (opts: Opts) => usersService.getAll({}, opts) },
      id: "upn",
      label: "fullName",
    },
    foiStageService
  );

  return (
    <>
      <FilterSearch
        title="Search..."
        value={searchParams.search}
        onChange={(value) =>
          navigateToNextSearch({
            search: value,
          })
        }
        dataTest={"search"}
      />
      &nbsp;&nbsp;
      <Filter
        Icon={FilterList}
        title="Order By"
        badge={!!searchParams.order}
        dataTest="foi-list-order"
      >
        <FilterRadioButton
          isChecked={searchParams.order === "DateReceived"}
          value={"DateReceived"}
          text={"Date received"}
          dataTest={"sort-date-received"}
          onChange={() =>
            navigateToNextSearch({
              order: "DateReceived",
              orderDirection: "desc",
            })
          }
        />
        <FilterRadioButton
          isChecked={searchParams.order === "CreatedDate"}
          value={"CreatedDate"}
          text={"Created date"}
          dataTest={"sort-created-date"}
          onChange={() =>
            navigateToNextSearch({
              order: "CreatedDate",
              orderDirection: "desc",
            })
          }
        />
        <FilterRadioButton
          isChecked={searchParams.order === "DaysLeft"}
          value={"DaysLeft"}
          text={"Due date"}
          dataTest={"sort-due-date"}
          onChange={() =>
            navigateToNextSearch({
              order: "DaysLeft",
              orderDirection: "asc",
            })
          }
        />
      </Filter>
      <FilterMenu
        Icon={FilterList}
        title="Filters"
        dataTest="filter"
        search={{ searchParamsFiltersOnly, navigateToNextSearch }}
      >
        {(workingParams, dirtyParams, addParam, updateMultipleParams) => (
          <form>
            <MenuItem className={classes.menuItem}>
              <Select
                className={
                  dirtyParams.includes("status") ? classes.root : undefined
                }
                label="Status"
                data-test="status-filter"
                value={workingParams.status}
                onChange={(e) => addParam("status", e.target.value)}
              >
                {statusLookups}
              </Select>
            </MenuItem>
            <MenuItem className={classes.menuItem}>
              <Select
                className={
                  dirtyParams.includes("stage") ? classes.root : undefined
                }
                label="Stage"
                data-test="stage-filter"
                value={workingParams.stage}
                onChange={(e) => addParam("stage", e.target.value)}
              >
                {foiStageLookups}
              </Select>
            </MenuItem>
            <MenuItem className={classes.menuItem}>
              <Select
                className={
                  dirtyParams.includes("type") ? classes.root : undefined
                }
                label="Type"
                data-test="type-filter"
                value={workingParams.type}
                onChange={(e) => addParam("type", e.target.value)}
              >
                {typeLookups}
              </Select>
            </MenuItem>
            <MenuItem className={classes.menuItem}>
              <Select
                className={
                  dirtyParams.includes("caseLead") ? classes.root : undefined
                }
                label="Case lead"
                data-test="case-lead-filter"
                selectVariant="multiple"
                value={ensureParamIsArray(workingParams.caseLead)}
                onChange={(e: any) => addParam("caseLead", e.target.value)}
                disabled={workingParams.unassignedOnly === "true"}
              >
                {usersLookups}
              </Select>
            </MenuItem>
            <FilterCheckbox
              isChecked={workingParams.unassignedOnly === "true"}
              name="unassignedOnly"
              dataTest="unassigned-checkbox"
              text="Unassigned only"
              onChange={() => {
                updateMultipleParams({
                  assignedTo: undefined,
                  unassignedOnly: workingParams.unassignedOnly
                    ? undefined
                    : "true",
                });
              }}
            />
            <MenuItem className={classes.menuItem}>
              <DatePicker
                label="Date received from:"
                value={workingParams.dateReceivedFrom || null}
                onChange={(val: any) => addParam("dateReceivedFrom", val)}
                onClear={() => addParam("dateReceivedFrom", undefined)}
                returnDayBoundary="startOfDay"
                dataTest="date-received-from-filter"
              />
            </MenuItem>
            <MenuItem className={classes.menuItem}>
              <DatePicker
                label="Date received to:"
                value={workingParams.dateReceivedTo || null}
                onChange={(val: any) => addParam("dateReceivedTo", val)}
                onClear={() => addParam("dateReceivedTo", undefined)}
                returnDayBoundary="endOfDay"
                dataTest="date-received-to-filter"
              />
            </MenuItem>
          </form>
        )}
      </FilterMenu>
    </>
  );
};
